import styled, { css } from '@xstyled/styled-components';
import React, { ChangeEventHandler, FC } from 'react';
import { Capsule, TickMarkCapsule } from '../capsules';

interface Props {
  variant: 'default' | 'checked';
}

const HiddenRadio = styled.input`
  clip-path: polygon(0 0);
  float: right;
`;

const Label = styled.labelBox<Props>`
  position: relative;
  display: flex;
  align-items: center;
  border-radius: ${p => p.theme.radii.md};
  cursor: pointer;
  font-size: ${p => p.theme.fontSizes.sm};
  transition: 0.2s all linear;

  ${Capsule} {
    margin-right: ${p => p.theme.space.md};
  }

  &:focus {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
    z-index: 1;
  }

  &:focus::after {
    content: '';
    display: block;
    position: absolute;
    border-radius: inherit;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid var(--color-focus-ring-inner);
    pointer-events: none;
  }

  ${p =>
    p.variant === 'checked'
      ? css`
          background: ${p.theme.colors.accentDark};
          font-weight: bold;
          font-size: md;
          color: var(--color-on-accent);
          border: 1px solid var(--color-accent-edge);
          ${Capsule} {
            background: var(--color-on-accent);
            > svg {
              fill: var(--color-accent-dk);
              stroke: var(--color-accent-dk);
            }
          }
        `
      : css`
          transition: 0.2s all linear;

          /* CAT-1241: designer — stroke #6F6F6F must pass vs #FAFAFA hover too */
          &:hover {
            background-color: var(--color-row-hover);
          }

          ${Capsule} {
            background: transparent;
            border: 1px solid ${p.theme.colors.greyDark};
          }
        `}
`;
// defaultProps for xstyled system props; cast to satisfy styled-component typings
(Label as React.ComponentType<unknown>).defaultProps = {
  py: { _: 'sm', sm: 'xs' },
  px: { _: 'sm', sm: 'sm' },
  // Always radio/capsule on the left — row-reverse on narrow viewports
  // pushed indicators to the far right (400% zoom / help Report an issue).
  flexDirection: 'row'
};

const Content = styled.span`
  flex: 1;
`;

const SubContent = styled.span`
  flex: 1;
  font-size: xs;
`;

export const PickListItem: FC<{
  checked?: boolean;
  value: string;
  additionalValue: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyPress: any;
  name: string;
}> = ({
  checked,
  children,
  value,
  onChange,
  name,
  onKeyPress,
  additionalValue
}) => {
  return (
    <Label
      variant={checked ? 'checked' : 'default'}
      tabIndex={0}
      onKeyPress={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          onKeyPress();
        }
      }}
      role="radio"
      aria-checked={Boolean(checked)}
    >
      {checked ? <TickMarkCapsule /> : <Capsule />}
      <Content>
        {children} <br />
        <SubContent>{additionalValue}</SubContent>
      </Content>
      <HiddenRadio
        tabIndex={-1}
        type="radio"
        checked={checked}
        value={value}
        onChange={onChange}
        name={name}
        aria-hidden="true"
      />
    </Label>
  );
};
